import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { PlusCircle } from "react-feather";
import {
  Banner,
  Button,
  Checkbox,
  Input,
  PageWrapperTwoColumn,
  Section,
  Select,
  SelectOption,
  Spinner,
} from "@cinesys/ui/components";
import CollapsibleFieldRow from "../CollapsibleFieldRow";
import SpotterHeader from "../SpotterHeader";
import SpotterChangesPanel, {
  SpotterChangeGroup,
  SpotterChangesPanelProps,
} from "../SpotterChangesPanel";
import TokenChip, { TokenCarousel, TokenRow } from "../TokenChip";
import { SPOTTER_STRINGS } from "../strings";
import { usePersistedState } from "../usePersistedState";
import { combineSpotterQueryErrors } from "../accessError";
import { useInterfaceAccess } from "../../../hooks/useInterfaceAccess";
import InterfaceAccessNotice from "../../../components/InterfaceAccessNotice";
import { DelayedSpinner } from "@cinesys/experience-ui/components";
import { SPOTTER_INTERFACE_ID } from "../config";
import {
  filterTemplateTokenNames,
  insertTemplateToken,
  openTemplateTokenQuery,
} from "../templateTokens";
import { collidingFieldNames, tokenNameFor } from "./tokenIdentity";
import { filterToMappedFields } from "./mappedFields";
import { useMapping } from "../mapping/service";
import {
  IconikMetadataField,
  IconikMetadataView,
  RenameCategory,
  RenameFieldToken,
  RenameSettings,
  useRenameIconikViews,
  useRenameSettings,
  useRenameTokenViews,
  useSaveRenameSettings,
} from "./service";

/**
 * Spotter "File Rename" tab (Monday 12610379991, `PLAN-spotter-file-rename-v3.md`) — a fourth
 * Spotter tab alongside Field Mapping / Glossary & AI Context / Gamebook, where the customer (not
 * CineSys staff) configures the per-category filename templates n8n uses to rename the on-prem
 * original file once tagging finishes (spec §2/§3.2).
 *
 * This is a Spotter tab, not an `apps/frontend` Portal-admin plugin variable — see
 * `PLAN-spotter-file-rename-v3.md`'s "Revision note" for the full rationale. There is no
 * `apps/frontend` plugin variable for this feature's trigger — n8n self-triggers the rename off
 * Phase 3 completion (a metadata-field flag), so the manager never needs to call out via a URL to
 * fire it; everything else is here now.
 *
 * Reuses Field Mapping's established patterns rather than inventing new ones (per the plan
 * and this session's own "reuse existing generic widgets" lesson):
 *   - `../TokenChip.tsx`'s `TokenChip`/`TokenCarousel` — insertable `{Token}` chips for a
 *     template textarea, same click-to-insert behavior (`../templateTokens.ts`'s
 *     `insertTemplateToken` — appends, or replaces an in-progress hand-typed `{partial` chip
 *     search) and the same curly-brace token syntax, shared verbatim with Fields' Formatting
 *     mode template editor (`../fields/FieldsScreen.tsx`).
 *   - A live "Resolves to" preview line, substituting illustrative sample values for each token —
 *     same job as Fields' `resultForEntry`.
 *   - `SpotterChangesPanel`/`SpotterHeaderChanges` — the same save/cancel/dirty-tracking UX as
 *     every other Spotter settings screen.
 *
 * Each category/template is its own `../CollapsibleFieldRow.tsx` card — the same collapsible-row
 * shell Field Mapping and Facial Recognition use (chevron/label/remove in the header, template
 * editor + chip carousel + preview in the collapsible body) — rather than `ListInput`'s old
 * flat-list-with-a-trailing-X-button treatment. The list of cards isn't wrapped in its own outer
 * `Section`; `TemplatesSectionHeader` is a plain, un-boxed heading instead. Only Rename's row
 * SHELL is shared this way — its search-less header and two-step button→pending-card→inline-
 * picker Add flow (below) stay exactly as they are, not Fields'/Facial Recognition's one-step
 * `AddOptionSelect` pattern.
 *
 * ADD-A-TEMPLATE: a `Button` (`AddTemplateWrap`), matching Field Mapping's "Add a new mapping"
 * flow — `FieldsScreen.tsx`'s `handleAddField`/`newFieldOrder`/`isPendingId` — rather than a bare
 * `Select` picking and adding a category in one step. Clicking it appends a `PENDING_PREFIX`-
 * tagged id to `pendingCategoryIds` (this file's `newFieldOrder` analogue) instead of resolving a
 * category immediately. A pending id renders its own `PendingCategoryRow` card
 * (`newTemplateTitle` in place of a real label) with an inline category `Select` — not a bare
 * select floating next to the button — and resolves into the normal `CategoryRow` template editor
 * the instant a category's picked. The "all categories claimed" empty state
 * (`AllCategoriesAddedNote`, inline in `TemplatesSectionHeader` past the divider rule) is
 * unaffected.
 *
 * `handleAddTemplate` scrolls the page to its bottom once the new pending card mounts
 * (`scrollWindowToBottom`, `PendingCategoryRow`'s `isJustAdded` effect). Rows default collapsed;
 * a freshly-added pending card or a just-resolved category auto-expands
 * (`expandedCategoryKeys`), mirroring Field Mapping/Facial Recognition's own auto-expand-on-add.
 * "Remove" on both `CategoryRow` and `PendingCategoryRow` plays `CollapsibleFieldRow`'s own
 * fade/collapse, and the actual removal is deferred until its real `onTransitionEnd` fires
 * (`removingCategoryKeys`), not a timer.
 *
 * REAL TOKENS, NOT A FIXED SAMPLE VOCABULARY (Monday 12624047544, items 2.3/2.4/2.6 — supersedes
 * this feature's original round, which offered a hardcoded 9-token list with fixed illustrative
 * sample values, identical for every org). Tokens come from the SAME two CineSys-configured views
 * (`mappingAssetViewId`/`mappingSegmentViewId`) `FieldsScreen.tsx` draws its destination fields
 * from — fetched via `useRenameTokenViews()` (`GET /iconik-views`, same endpoint Field Mapping
 * uses). This is a DIFFERENT query than the Original Name Field picker's own `useRenameIconikViews()`
 * (`GET /iconik-views/rename`, unfiltered — see that section below): the two-view assumption this
 * token feature depends on (at most one view per scope, so `assetView`/`segmentView`'s `.find()`
 * below is safe) only holds for the two-view-scoped response, not the Original Name Field's
 * unrestricted one.
 *
 * MAPPED-ONLY, ONE CAROUSEL (Slack, Addison — "lets only show mapped fields, not all... remove
 * the sections, make it one carosel", zero-mapped-fields case: no empty-state hint, just show
 * whatever remains). `flatFieldEntries` below is every real field on the two configured views,
 * unfiltered — still what `handleSave`'s `computeFieldTokens` uses, so an existing template's
 * field-identity record survives even if that field later becomes unmapped. What the carousel
 * itself OFFERS is a further-filtered `mappedFieldEntries` (`./mappedFields.ts`'s
 * `filterToMappedFields`, cross-referenced against `useMapping()`'s `GET /spotter/mapping`
 * response — already pre-filtered to non-empty, actually-mapped entries) — asset- and
 * segment-scope fields merged into one flat list, no per-scope section/divider. `{ClipNumber}` is
 * the one exception to the "real field" story: it's n8n-computed per batch, not a real iconik
 * field, so it has no mapping/field identity to filter on — it's unconditionally appended to that
 * same merged list (`CategoryRow`) and rendered as a completely ordinary chip, no special styling
 * or label (dropped along with the old per-section dividers).
 *
 * FIELD IDENTITY (item 2.4) — `RenameSettings.fieldTokens` (see `service.ts`'s doc comment for the
 * `{field_id, scope, view_id}` shape) is computed fresh from the live view catalog at SAVE time
 * (`computeFieldTokens`, called from `handleSave`) rather than tracked as editable UI state — it
 * isn't something the customer edits, it's a derived fact about which real field each token names,
 * so it's excluded from the dirty-check/Changes-panel machinery entirely (recomputing it never
 * produces a spurious "unsaved change"). `view_id` is populated from `FlatFieldEntry.view.id` here
 * for shape completeness, but the server never trusts it as submitted — see `rename.ts`'s
 * `reconcileFieldTokenViewIds`.
 *
 * TOKEN-TEXT COLLISIONS (Monday 12645331451, item 2) — the same field NAME can legitimately exist
 * on both the asset-scope and segment-scope configured views (`PORTAL-INTERFACE.md` §5.4: they're
 * different views, so "GameID" on each is a different value to resolve). `./tokenIdentity.ts`'s
 * `tokenNameFor` gives every `FlatFieldEntry` a `tokenName` — normally just `field.name`, but a
 * segment-scope field colliding with an asset-scope field of the same name gets a `Timed:` prefix,
 * so the two never collapse onto one ambiguous `{FieldName}` token / `fieldTokens` key. Asset-scope
 * tokens are never qualified, so an already-saved template's bare `{FieldName}` keeps resolving the
 * same way it always did in the (common) non-colliding case.
 *
 * Categories are much simpler than Mapping's per-destination-field rows: one whole settings
 * object per org (3ish templates + 4 flat options), not a large per-field collection — so this
 * page has no search bar and no per-field mapping table, and its dirty-tracking (`changeGroups`
 * below) is two coarse groups ("Templates" / "Options"), not one row per individual field the way
 * Mapping's is.
 *
 * ORIGINAL NAME FIELD — one `Select`, options flattened across every one of the org's views (via
 * `useRenameIconikViews()`, `GET /iconik-views/rename` — every view in any of the org's metadata-
 * view categories, deliberately NOT restricted to Field Mapping's two CineSys-configured views;
 * see that hook's doc comment in `service.ts`) with the view name as the option's `group`. Mirrors
 * the `${viewId}::${fieldName}` composite-value / view-as-group pattern
 * `MetadataViewFieldPicker.tsx` + `selectOption.ts`'s `metadata_view_fields` branch use in
 * `apps/frontend` — NOT the two-step "pick a view, then a field within it" pattern
 * `SpotterViewPicker.tsx`/`FieldsScreen.tsx` use elsewhere in this app. The full
 * `{field_id, scope, view_id}` identity is persisted (`RenameSettings.originalNameField`) rather
 * than just the bare field NAME, since a bare name can't tell apart a same-named field on two
 * different views — see `service.ts`'s `RenameFieldToken` doc comment — computed from the
 * composite value's own `viewId`/`fieldName` at select time (`parseFieldOptionValue`).
 *
 * No pre-seeded defaults: a first-time visitor (nothing saved yet — `useRenameSettings()`
 * resolves to `null`) starts from a genuinely empty settings object — zero category options, zero
 * category rows. The init effect below still seeds BOTH `settings` and its dirty-check baseline
 * from whatever `useRenameSettings()` resolves to (empty or already-saved), so merely loading the
 * screen is never itself counted as an unsaved change (same "baseline = whatever was just loaded"
 * technique `FieldsScreen.tsx`'s own init effect uses).
 *
 * CATEGORY OPTIONS: each category row's name (`RenameCategory.label`) is chosen, not typed
 * freehand, from the pending card's inline `Select` at the time it's resolved (see the
 * ADD-A-TEMPLATE PATTERN section above) — iconik has no
 * built-in canonical list of footage categories/formats to draw from (a custom iconik field can
 * carry its own `options`, but that's per-org and not wired to anything here). Addison's
 * follow-up call after this tab shipped: this list is NOT customer-editable — it's a
 * CineSys-configured plugin variable (`renameCategoryOptions`,
 * `apps/backend/src/definitions/plugins/spotter.ts`), read-only off `useRenameSettings()`'s
 * `categoryOptions` (see `service.ts`'s `RenameSettingsResponse`) — no add/remove UI for the
 * option list itself on this tab, only for which of those options currently have a template row
 * (`availableCategoryOptions` = `categoryOptions` minus every already-added row's label).
 */

/** Same page-scoped font-family/size fix as `FieldsScreen.tsx`'s `Page` — see that file's doc
 *  comment for the full "why". */
const Page = styled.div`
  min-height: 100%;
  color: var(--spotterInk);
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
`;

/** Same two-column layout + Select/Button/Checkbox re-skin + local-portal-host technique as
 *  `FieldsScreen.tsx`'s `Wrap` — see that file's doc comment for the full "why". */
const Wrap = styled.div`
  padding: 0 22px;
  max-width: 1520px;
  margin: 0 auto;

  --inputBackground: var(--spotterRaised);
  --inputBorder: var(--spotterLine2);
  --dark400: var(--spotterRaised);
  --dark500: var(--spotterLine2);
  --dark600: var(--spotterLine2);
  --dark700: var(--spotterInkFaint);
  --gray400: var(--spotterInk);
  --gray500: var(--spotterInk);
  --blue060: var(--spotterAccent);
  --blue100: var(--spotterAccent);
  --selectOptionHover: var(--spotterAccentHover);

  --blue400: var(--spotterAccent);
  --blue500: var(--spotterAccent);
  --blueBorder: var(--spotterAccentLine);
  --blueBorderOverlay: var(--spotterAccentLine);
  --blueBackground: var(--spotterAccentSoft);
  --blueFocusRing: var(--spotterAccentSoft);

  --green400: var(--spotterOk);
  --green500: var(--spotterOk);
  --greenBorder: var(--spotterOkLine);
  --greenBorderOverlay: var(--spotterOkLine);
  --greenBackground: var(--spotterOkSoft);

  --red400: var(--spotterDanger);
  --red500: var(--spotterDanger);
  --redBorder: var(--spotterDangerBorder);
  --redBorderOverlay: var(--spotterDangerBorder);
  --redBackground: var(--spotterDangerSoft);
`;

const Main = styled.main`
  padding-top: 20px;
  padding-bottom: 20px;
`;

const SelectPortalHost = styled.div`
  display: contents;
`;

const Aside = styled.aside`
  padding-top: 20px;
  padding-bottom: 20px;

  @media (min-width: 1030px) {
    position: sticky;
    top: var(--spotterHeaderHeight, 98px);
    max-height: calc(100vh - var(--spotterHeaderHeight, 98px));
    overflow-y: auto;
    margin: 0 -14px;
    padding: 20px 14px 20px;
  }
`;

const AsideChangesPanel = styled.div`
  display: none;

  @media (min-width: 1030px) {
    display: block;
  }
`;

const Note = styled.div`
  display: flex;
  gap: 12px;
  padding: 13px 15px;
  border-radius: 10px;
  background: var(--spotterAccentSoft);
  border: 1px solid var(--spotterAccentLine);
  margin-bottom: 16px;
  font-size: 14px;
  color: var(--spotterInk);
  line-height: 1.5;
`;

const NoteRule = styled.div`
  width: 3px;
  border-radius: 3px;
  background: var(--spotterAccent);
  flex: 0 0 auto;
`;

/** Local padding for this screen's two `Section`s — applied here rather than via `Section`'s own
 *  `pad` prop so it doesn't change padding for every other `pad`-using `Section` in the app. */
const SectionBody = styled.div`
  padding: 10px 15px 0;
`;

/** The Options section, 2-up (Addison: "move options above the categories, and make it a 2 column
 *  layout") — collapses to a single column below the same breakpoint the two-column page layout
 *  itself stacks at, so it never gets cramped in the narrow/mobile view. Left column stacks the 3
 *  toggles (`OptionsCheckboxes`); right column is just the Original Name Field picker (Addison's
 *  follow-up: "stack all three checkboxes on the left column and only have the original file name
 *  on the right column"). */
const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;

  @media (max-width: 1030px) {
    grid-template-columns: 1fr;
  }
`;

const OptionsCheckboxes = styled.div`
  display: flex;
  flex-direction: column;
`;

const PreviewLine = styled.div`
  margin-top: 9px;
  padding: 9px 12px;
  border-radius: 8px;
  background: var(--spotterPaper);
  color: #e6eaee;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 14px;
  display: flex;
  gap: 10px;
  align-items: center;
`;

const PreviewLabel = styled.span`
  color: #8f989f;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

/** No longer rendered directly in `CategoryRow` — the token carousel merged down to one
 *  undivided list (see the file doc comment's "MAPPED-ONLY, ONE CAROUSEL" section), so this only
 *  survives as `TemplatesSectionHeader`'s base style now. Kept as its own styled component (mirrors
 *  `FieldsScreen.tsx`'s own private `TimedMetadataHeader`) rather than folded into
 *  `TemplatesSectionHeader` directly, since the "small underlined label, divider rule trailing
 *  off to the right" look is still a reusable primitive, not a one-off. */
const TimedMetadataHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 8px;
  font-size: 12px;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: var(--spotterInkFaint);
  font-family: ui-monospace, Menlo, monospace;
  font-weight: 700;

  &::after {
    content: "";
    flex: 1 1 auto;
    height: 1px;
    background: var(--spotterLine2);
  }
`;

const TemplatesHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  height: 39px;
  margin-top: 22px;
  margin-bottom: 10px;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
    height: auto;
  }
`;

const TemplatesSectionHeader = styled(TimedMetadataHeader)`
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
`;

/** Holds the "Add a new file name template" button, not a select anymore — fixed/natural width
 *  so `TemplatesSectionHeader` (flex: 1 1 0) fills the rest of the row, mirroring Field Mapping's
 *  own `AddFieldWrap` (`FieldsScreen.tsx`). */
const AddTemplateWrap = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AllCategoriesAddedNote = styled.span`
  order: 1;
  flex-shrink: 0;
  white-space: nowrap;
`;

/** A pending (not-yet-resolved) template card's own inline category picker — mirrors Field
 *  Mapping's `DestinationFieldWrap` (`FieldsScreen.tsx`). */
const CategoryPickerWrap = styled.div`
  margin-bottom: 4px;
`;

/** The n8n-computed per-batch clip counter — the ONE token that is NOT a real customer iconik
 *  field (see the file doc comment's "REAL TOKENS" section), so it's a fixed constant rather than
 *  something derived from `useRenameIconikViews()` like every other token now is. `{ClipNumber}`
 *  is deliberately never a key in `RenameSettings.fieldTokens` — it has no field identity to
 *  record (see `service.ts`'s `RenameFieldToken` doc comment). */
const CLIP_NUMBER_TOKEN = "{ClipNumber}";
/** Illustrative sample for the "Resolves to" preview only — never sent to the server, matches the
 *  value this token has always previewed as (unchanged by this round's real-field rework). */
const CLIP_NUMBER_SAMPLE = "001";

/** iconik metadata `field_type` values that carry an `options: [{label, value}]` list to preview a
 *  sample value from. Broader than `apps/customer-api/src/routes/spotter/glossary.ts`'s
 *  `GLOSSARY_ELIGIBLE_FIELD_TYPES` on purpose — this is just an illustrative preview, not a
 *  bounded-value guarantee, so `tag_cloud` (free-entry even when curated) is fine here even though
 *  it doesn't qualify for Glossary. */
const PICKLIST_FIELD_TYPES = new Set(["drop_down", "tag_cloud"]);

/** Placeholder for a token with no real illustrative value to show (a free-text field, or a
 *  picklist field whose `options` happens to be empty) — the established "no value" convention
 *  already used across this page (this file's own template-preview default) and Mapping's preview
 *  cards (`FieldsScreen.tsx`'s `resultForEntry(...).join(", ") || "—"`), not a new one invented
 *  for this round. */
const EMPTY_TOKEN_PLACEHOLDER = "—";

/** One field within one of the org's two configured views — mirrors `FieldsScreen.tsx`'s own
 *  `FlatFieldEntry` (kept here at module scope, not nested inside a component body like Mapping's,
 *  since `CategoryRow` — a sibling component — needs the type too). Every real field on the two
 *  views, unfiltered; `CategoryRow`'s `TokenCarousel` only ever sees the further-filtered,
 *  mapped-only subset (`./mappedFields.ts`'s `filterToMappedFields` — see the file doc comment's
 *  "MAPPED-ONLY, ONE CAROUSEL" section). */
export interface FlatFieldEntry {
  view: IconikMetadataView;
  field: IconikMetadataField;
  /** The literal `{...}` token text for this field — usually `field.name`, qualified on a
   *  same-name asset/segment collision (see the file doc comment's "TOKEN-TEXT COLLISIONS"
   *  section and `./tokenIdentity.ts`). This, not `field.name`, is what's inserted into a
   *  template and used as the `fieldTokens` map key. */
  tokenName: string;
}

/** Illustrative sample value for a real field's token — item 2.6: a picklist field's (`drop_down`/
 *  `tag_cloud`) first curated option, otherwise (free-text, or a picklist with no `options`
 *  configured) the neutral placeholder. Never sent to the server, never part of the wire contract
 *  — same "preview only" convention `FieldsScreen.tsx`'s `sampleValueFor` uses. */
const sampleValueForField = (field: IconikMetadataField): string => {
  if (PICKLIST_FIELD_TYPES.has(field.field_type) && field.options?.length) {
    return field.options[0].value;
  }
  return EMPTY_TOKEN_PLACEHOLDER;
};

/** `{token}`-substitution preview for one category's template — unresolved tokens (a typo, or a
 *  field that no longer exists on either configured view) are left as literal text rather than
 *  blanked, so a customer can see exactly what didn't resolve (unchanged behavior from the fixed-
 *  vocabulary version). Deliberately has no notion of "mapped" — `CategoryRow` always calls this
 *  with `allFieldEntries` (unfiltered), never the carousel's mapped-only `fieldEntries`, precisely
 *  so a field that's since become unmapped still resolves here (see that prop's doc comment).
 *  Exported for `__tests__/resultForTemplate.unit.test.ts`, which pins exactly that behavior. */
export const resultForTemplate = (
  template: string,
  fieldEntries: FlatFieldEntry[]
): string => {
  let filled = template;
  for (const { field, tokenName } of fieldEntries) {
    filled = filled.split(`{${tokenName}}`).join(sampleValueForField(field));
  }
  return filled.split(CLIP_NUMBER_TOKEN).join(CLIP_NUMBER_SAMPLE);
};

const emptySettings = (): RenameSettings => ({
  categories: [],
  replaceAssetTitle: false,
  includeFileExtensionInTitle: false,
  originalNameField: "",
  useMetadataValues: false,
  fieldTokens: {},
});

const settingsEqual = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/** Tags a `pendingCategoryIds` entry as a not-yet-resolved "add a new template" card — mirrors
 *  Field Mapping's own `PENDING_PREFIX`/`isPendingId` (`FieldsScreen.tsx`). */
const PENDING_PREFIX = "pending::";

/** Scrolls the whole PAGE to its true bottom when a template is added — mirrors Field Mapping's
 *  own `scrollWindowToBottom` (`FieldsScreen.tsx`) exactly, rather than
 *  `element.scrollIntoView`ing the new card itself, since a card's own bottom edge isn't
 *  necessarily the page's actual bottom edge. */
const scrollWindowToBottom = (): void => {
  window.scrollTo({
    top: document.documentElement.scrollHeight,
    behavior: "smooth",
  });
};

/** Composite view key — mirrors `FieldsScreen.tsx`'s `viewKey` (a bare view `id` can repeat
 *  across an asset view and a segment view; see that file's doc comment for the full story). */
const viewKey = (view: { id: string; scope: "asset" | "segment" }): string =>
  `${view.id}::${view.scope}`;

/** One Original Name Field option's composite value — `${viewId}::${scope}::${fieldName}`.
 *  Mirrors `apps/frontend`'s (unmerged) `MetadataViewFieldPicker.tsx`'s `optionValue`/
 *  `parseOptionValue` convention, extended with `scope` per `viewKey`'s doc comment above. */
const fieldOptionValue = (
  view: { id: string; scope: "asset" | "segment" },
  fieldName: string
): string => `${viewKey(view)}::${fieldName}`;

/** Splits a `fieldOptionValue` composite back into its parts — only `fieldName` is actually used
 *  (that's the only part `RenameSettings.originalNameField` stores), but the view/scope are parsed
 *  out too for symmetry with the value-building side. `fieldName` can itself contain `::`
 *  (uncommon, but iconik field names aren't restricted), so it's everything after the first two
 *  segments, not a naive 3-way split. */
const parseFieldOptionValue = (
  value: string
):
  | { viewId: string; viewScope: "asset" | "segment"; fieldName: string }
  | undefined => {
  const first = value.indexOf("::");
  if (first < 0) {
    return undefined;
  }
  const second = value.indexOf("::", first + 2);
  if (second < 0) {
    return undefined;
  }
  return {
    viewId: value.slice(0, first),
    viewScope: value.slice(first + 2, second) as "asset" | "segment",
    fieldName: value.slice(second + 2),
  };
};

const CategoryRow: React.FC<{
  value: RenameCategory;
  onChange: (value: RenameCategory) => void;
  /** Removes this category from the list. */
  onRemove: () => void;
  /** The org's MAPPED real fields only (asset- and segment-scope merged, no per-scope split) —
   *  what the chip carousel OFFERS to insert. See the file doc comment's "MAPPED-ONLY, ONE
   *  CAROUSEL" section. `{ClipNumber}` is appended separately below since it has no field
   *  identity to filter/mix in here. */
  fieldEntries: FlatFieldEntry[];
  /** Every real field, UNFILTERED — used only by the "Resolves to" preview line below, so a
   *  template referencing a field that's since become unmapped (or was hand-typed rather than
   *  chip-inserted) still resolves a sample value instead of silently going unresolved. Mirrors
   *  `handleSave`'s `computeFieldTokens(flatFieldEntries)`, which the same identity-persistence
   *  reasoning already applies to — the carousel's own visibility is the only thing scoped to
   *  mapped fields, not what a saved/typed token is allowed to resolve to. */
  allFieldEntries: FlatFieldEntry[];
  /** True while this category has no counterpart in the last-saved settings. */
  isNew?: boolean;
  /** True while this category HAS a counterpart in the last-saved settings but differs from it. */
  isEdited?: boolean;
  /** True for exactly one render, right after this specific category was just added — triggers
   *  the scroll-into-view-and-focus effect below, then this component tells the parent to clear
   *  the flag via \`onAutoFocused\` so it doesn't refire on every subsequent edit to this same row. */
  autoFocus?: boolean;
  onAutoFocused?: () => void;
  /** True once "Remove" has been clicked for this row — the actual removal is deferred until the
   *  collapse animation finishes, so there's something to animate away instead of an instant
   *  unmount. */
  isRemoving?: boolean;
  /** Fired once the removal animation's finished — the parent does the actual
   *  `settings.categories` filter here, plus clears its own removing-keys bookkeeping. */
  onRemoveTransitionEnd?: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
}> = ({
  value,
  onChange,
  onRemove,
  fieldEntries,
  allFieldEntries,
  isNew,
  isEdited,
  autoFocus,
  onAutoFocused,
  isRemoving,
  onRemoveTransitionEnd,
  expanded,
  onToggleExpand,
}) => {
  // Clicking a chip moves focus to the chip's own button — refocus the template input afterward
  // so the customer can keep typing/inserting without a manual click back into it.
  const templateInputRef = useRef<HTMLInputElement>(null);
  const insertToken = (token: string): void => {
    onChange({
      ...value,
      template: insertTemplateToken(value.template, token),
    });
    templateInputRef.current?.focus();
  };

  useEffect(() => {
    if (!autoFocus) {
      return;
    }
    const el = templateInputRef.current;
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    el?.focus({ preventScroll: true });
    onAutoFocused?.();
  }, [autoFocus]);

  // Live-filter the chip row while the customer is hand-typing a `{token}` — see
  // `templateTokens.ts`'s `openTemplateTokenQuery`/`filterTemplateTokenNames` doc comments.
  // `tokenName`s are already collision-qualified (see the file doc comment's "TOKEN-TEXT
  // COLLISIONS" section), so one shared fuzzy search across the whole merged list is safe — no
  // per-scope split needed now that there's only one carousel.
  const tokenQuery = openTemplateTokenQuery(value.template);
  const matchedNames = new Set(
    filterTemplateTokenNames(
      fieldEntries.map((e) => e.tokenName),
      tokenQuery
    )
  );
  const filteredFieldEntries = fieldEntries.filter((e) =>
    matchedNames.has(e.tokenName)
  );
  const clipNumberMatchesQuery =
    filterTemplateTokenNames([CLIP_NUMBER_TOKEN.slice(1, -1)], tokenQuery)
      .length > 0;

  return (
    <CollapsibleFieldRow
      isNew={isNew}
      isEdited={isEdited}
      isRemoving={isRemoving}
      onRemoveTransitionEnd={onRemoveTransitionEnd}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      expandTitle={SPOTTER_STRINGS.rename.expandRowTitle}
      collapseTitle={SPOTTER_STRINGS.rename.collapseRowTitle}
      label={value.label}
      summary={value.template || undefined}
      removeLabel={SPOTTER_STRINGS.rename.removeTemplateButton}
      onRemoveClick={onRemove}
    >
      <Input
        ref={templateInputRef}
        noPadding
        label={SPOTTER_STRINGS.rename.templateLabel}
        placeholder={SPOTTER_STRINGS.rename.templatePlaceholder}
        value={value.template}
        onChange={(template) => onChange({ ...value, template })}
      />
      <TokenRow>
        {filteredFieldEntries.length > 0 || clipNumberMatchesQuery ? (
          <TokenCarousel
            gap={8}
            arrowSize={26}
            arrowIconSize={16}
            justify="start"
            fadeColor="var(--spotterSurface)"
          >
            {filteredFieldEntries.map(({ tokenName }) => (
              <TokenChip
                key={tokenName}
                token={`{${tokenName}}`}
                onClick={() => insertToken(`{${tokenName}}`)}
              />
            ))}
            {clipNumberMatchesQuery && (
              <TokenChip
                token={CLIP_NUMBER_TOKEN}
                onClick={() => insertToken(CLIP_NUMBER_TOKEN)}
              />
            )}
          </TokenCarousel>
        ) : (
          <Banner placeholder={SPOTTER_STRINGS.noTokenOptions} />
        )}
      </TokenRow>
      <PreviewLine>
        <PreviewLabel>{SPOTTER_STRINGS.rename.previewLabel}</PreviewLabel>
        <b>
          {value.template
            ? resultForTemplate(value.template, allFieldEntries)
            : "—"}
        </b>
      </PreviewLine>
    </CollapsibleFieldRow>
  );
};

/** A `pendingCategoryIds` row — clicked "Add a new file name template" button, no category chosen
 *  yet. Kept as its own small component rather than folded into `CategoryRow` — there's no
 *  `RenameCategory` to edit yet, just a category picker. Resolving (picking a category) hands off
 *  to `onResolve`, which appends the real `CategoryRow` and drops this pending id. */
const PendingCategoryRow: React.FC<{
  availableCategoryOptions: string[];
  onResolve: (label: string) => void;
  onRemove: () => void;
  selectPortal: HTMLDivElement | null;
  /** Same deferred-removal dance as `CategoryRow`'s own `isRemoving`/`onRemoveTransitionEnd`. */
  isRemoving?: boolean;
  onRemoveTransitionEnd?: () => void;
  /** True for exactly one render, right after "Add a new file name template" was clicked and
   *  this card was the one appended — scrolls the page to the bottom so the customer doesn't
   *  have to hunt for it, then clears itself via `onJustAddedHandled` (mirrors `CategoryRow`'s
   *  own `autoFocus`/`onAutoFocused` one-shot-flag convention above). */
  isJustAdded?: boolean;
  onJustAddedHandled?: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
}> = ({
  availableCategoryOptions,
  onResolve,
  onRemove,
  selectPortal,
  isRemoving,
  onRemoveTransitionEnd,
  isJustAdded,
  onJustAddedHandled,
  expanded,
  onToggleExpand,
}) => {
  useEffect(() => {
    if (!isJustAdded) {
      return;
    }
    scrollWindowToBottom();
    onJustAddedHandled?.();
  }, [isJustAdded]);

  return (
    <CollapsibleFieldRow
      isNew
      isRemoving={isRemoving}
      onRemoveTransitionEnd={onRemoveTransitionEnd}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      expandTitle={SPOTTER_STRINGS.rename.expandRowTitle}
      collapseTitle={SPOTTER_STRINGS.rename.collapseRowTitle}
      label={SPOTTER_STRINGS.rename.newTemplateTitle}
      removeLabel={SPOTTER_STRINGS.rename.removeTemplateButton}
      onRemoveClick={onRemove}
    >
      <CategoryPickerWrap>
        <Select
          label={SPOTTER_STRINGS.rename.addTemplateSelectLabel}
          placeholder={SPOTTER_STRINGS.rename.addTemplatePlaceholder}
          isSearchable
          options={availableCategoryOptions.map((option) => ({
            value: option,
            label: option,
          }))}
          value={null}
          onChange={(option) => {
            const label = (option as SelectOption)?.value as string | undefined;
            if (label) {
              onResolve(label);
            }
          }}
          portalTarget={selectPortal}
          noPadding
          autoFocus
        />
      </CategoryPickerWrap>
    </CollapsibleFieldRow>
  );
};

const RenameScreen = () => {
  const access = useInterfaceAccess(SPOTTER_INTERFACE_ID);
  const settingsQuery = useRenameSettings();
  const viewsQuery = useRenameIconikViews();
  const tokenViewsQuery = useRenameTokenViews();
  // Which real fields are actually mapped (Field Mapping's own data) — filters the token
  // carousel down to mapped fields only, see the file doc comment's "MAPPED-ONLY, ONE CAROUSEL"
  // section. Same `requirePlugin("spotter-mapping")` gate already covers this whole `/spotter`
  // route tree, so this introduces no new access-control case for an org that can load this tab.
  const mappingQuery = useMapping();
  const saveMutation = useSaveRenameSettings();

  // Persisted across tab switches (`usePersistedState`) — `RenameScreen` unmounts when navigating
  // to another Spotter tab, same reasoning as `FieldsScreen.tsx`.
  const [settings, setSettings] = usePersistedState<RenameSettings>(
    "spotter-rename-settings",
    emptySettings()
  );
  const [originalSettings, setOriginalSettings] =
    usePersistedState<RenameSettings>(
      "spotter-rename-settings-original",
      emptySettings()
    );
  const [initialized, setInitialized] = usePersistedState(
    "spotter-rename-initialized",
    false
  );
  // Every `Select` on this page (Original Name Field, and each category row's Category picker)
  // sits inside a `Section`, which clips overflow (`overflow: hidden`) — same "local portal host
  // escapes clipping without leaking outside this page" technique as `FieldsScreen.tsx`'s `Wrap`
  // doc comment describes.
  const [selectPortal, setSelectPortal] = useState<HTMLDivElement | null>(null);
  // Set right after a category's added via the add-template `Select`, cleared by the matching
  // `CategoryRow` itself once it's scrolled/focused into view (`CategoryRow`'s `onAutoFocused`).
  // Not persisted (unlike `settings` above) — a one-shot UI cue, meaningless across a tab
  // switch/remount.
  const [justAddedCategoryLabel, setJustAddedCategoryLabel] = useState<
    string | null
  >(null);
  // Ids of not-yet-resolved template cards ("Add a new file name template" clicked, no category
  // picked yet) — mirrors Field Mapping's own `newFieldOrder`/`PENDING_PREFIX`/`isPendingId`
  // convention in `FieldsScreen.tsx`. Persisted like `settings` above so
  // switching tabs and back doesn't lose an in-progress add. A pending row isn't a `RenameCategory`
  // (no label chosen yet), so it's tracked here rather than in `settings.categories` — nothing
  // pending is ever saved.
  const [pendingCategoryIds, setPendingCategoryIds] = usePersistedState<
    string[]
  >("spotter-rename-pending-categories", []);
  const pendingIdCounterRef = useRef(0);
  // Set right after "Add a new file name template" is clicked, cleared by the new pending card's
  // own effect once it's scrolled into view (`PendingCategoryRow`'s `onJustAddedHandled`). Not
  // persisted — a one-shot UI cue, same convention as `justAddedCategoryLabel` above.
  const [justAddedPendingId, setJustAddedPendingId] = useState<string | null>(
    null
  );
  // Keys currently mid-"Remove" exit animation (`CollapsibleFieldRow`'s own fade/collapse).
  // Holds EITHER a resolved category's own `label` or a still-pending card's id — the two id
  // spaces never collide (see `PENDING_PREFIX`'s doc comment). The actual removal (dropping from
  // `settings.categories`/`pendingCategoryIds`) is deferred until the animation finishes, not run
  // on click, so there's something to animate away.
  const [removingCategoryKeys, setRemovingCategoryKeys] = useState<Set<string>>(
    new Set()
  );
  // Which rows are expanded, keyed the same way as `removingCategoryKeys` (a category's `label`
  // or a pending card's id). Rows default collapsed; a freshly-added pending card or a
  // just-resolved category gets added here so it opens straight into view instead of requiring
  // an extra click, mirroring Field Mapping/Facial Recognition's own auto-expand-on-add.
  const [expandedCategoryKeys, setExpandedCategoryKeys] = useState<Set<string>>(
    new Set()
  );
  const toggleExpandedCategory = (key: string): void => {
    setExpandedCategoryKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!initialized && settingsQuery.data !== undefined) {
      // `settingsQuery.data.settings` is `RenameSettings | null` — `null` means nothing saved yet,
      // so this seeds a genuinely empty settings object as BOTH the working value and the
      // dirty-check baseline (see the file doc comment for why no pre-seeded defaults).
      const initial = settingsQuery.data?.settings ?? emptySettings();
      setSettings(initial);
      setOriginalSettings(initial);
      setInitialized(true);
    }
  }, [initialized, settingsQuery.data]);

  // Org-level entitlement gate — see FieldsScreen.tsx's equivalent doc comment.
  if (access.status !== "granted") {
    return (
      <Page>
        <SpotterHeader />
        {access.status === "denied" && (
          <Wrap>
            <InterfaceAccessNotice reason={access.reason} />
          </Wrap>
        )}
        {access.status === "pending" && <DelayedSpinner padTop />}
      </Page>
    );
  }

  const isLoading =
    settingsQuery.isLoading ||
    viewsQuery.isLoading ||
    tokenViewsQuery.isLoading ||
    mappingQuery.isLoading;

  if (isLoading) {
    return (
      <Page>
        <SpotterHeader />
        <Spinner padTop />
      </Page>
    );
  }

  const { hasError, accessDenied } = combineSpotterQueryErrors([
    settingsQuery,
    viewsQuery,
    tokenViewsQuery,
    mappingQuery,
  ]);

  if (hasError) {
    return (
      <Page>
        <SpotterHeader />
        <Wrap>
          <Banner
            error={
              accessDenied
                ? SPOTTER_STRINGS.rename.accessDeniedError
                : SPOTTER_STRINGS.rename.loadError
            }
            padTop
          />
        </Wrap>
      </Page>
    );
  }

  const views = viewsQuery.data ?? [];
  const tokenViews = tokenViewsQuery.data ?? [];
  // ASSET-scope fields first, then SEGMENT-scope ("Timed Metadata") fields — the field-TOKEN
  // list's ordering (see the file doc comment's "REAL TOKENS" section). Sourced from
  // `tokenViews` (the two-view-scoped query), NOT `views` (the Original Name Field's unfiltered
  // query) — `.find()` picking a single view per scope is only safe when at most one view per
  // scope can come back, which the unfiltered category listing doesn't guarantee.
  const assetView = tokenViews.find((v) => v.scope === "asset");
  const segmentView = tokenViews.find((v) => v.scope === "segment");
  // Field names that need disambiguating — see the file doc comment's "TOKEN-TEXT COLLISIONS"
  // section and `./tokenIdentity.ts`.
  const collidingNames = collidingFieldNames(
    (assetView?.view_fields ?? []).map((f) => f.name),
    (segmentView?.view_fields ?? []).map((f) => f.name)
  );
  const fieldEntriesFor = (
    view: IconikMetadataView | undefined
  ): FlatFieldEntry[] =>
    view
      ? (view.view_fields ?? []).map((field) => ({
          view,
          field,
          tokenName: tokenNameFor(field.name, view.scope, collidingNames),
        }))
      : [];
  const flatFieldEntries: FlatFieldEntry[] = [
    ...fieldEntriesFor(assetView),
    ...fieldEntriesFor(segmentView),
  ];
  // What the token carousel actually OFFERS to insert — `flatFieldEntries` filtered down to
  // fields the org has mapped (see the file doc comment's "MAPPED-ONLY, ONE CAROUSEL" section).
  // `flatFieldEntries` itself stays unfiltered and is passed to `CategoryRow` too (as
  // `allFieldEntries`) for `computeFieldTokens` below AND the "Resolves to" preview line, so an
  // already-saved (or hand-typed) template's field identity/resolution survives even if that
  // field later becomes unmapped — only the carousel's VISIBILITY is mapped-only.
  const mappedFieldEntries = filterToMappedFields(
    flatFieldEntries,
    mappingQuery.data?.fields ?? []
  );
  // Every field-token's resolved identity, keyed by its `{Token}` text (`tokenName` — usually the
  // bare field `name`, no braces, but collision-qualified — see the file doc comment's "FIELD
  // IDENTITY"/"TOKEN-TEXT COLLISIONS" sections). `field_id` always stays the REAL field name
  // (`field.name`), even when the key itself is qualified — that's what n8n actually looks up in
  // iconik. Recomputed from the LIVE view catalog at save time (`handleSave` below), not tracked as
  // editable state.
  const computeFieldTokens = (
    entries: FlatFieldEntry[]
  ): Record<string, RenameFieldToken> =>
    Object.fromEntries(
      entries.map(({ field, view, tokenName }) => [
        tokenName,
        { field_id: field.name, scope: view.scope, view_id: view.id },
      ])
    );

  // One flat option per (view, field) pair, `group` = view name — see the file doc comment's
  // "ORIGINAL NAME FIELD" section for why this is a single select rather than a two-step picker.
  const originalNameFieldOptions = views
    .flatMap((v) =>
      v.view_fields.map((f) => ({
        value: fieldOptionValue(v, f.name),
        label: f.label || f.name,
        group: v.name,
        fieldName: f.name,
        viewId: v.id,
      }))
    )
    .sort((a, b) =>
      a.group === b.group
        ? a.label.localeCompare(b.label)
        : a.group.localeCompare(b.group)
    );
  // The Select's `value` must be one specific composite option value, not just the bare field
  // name — match the currently-saved reference's own `view_id`/`field_id`, not just field name
  // (see the file doc comment's "ORIGINAL NAME FIELD" section on why the full identity is stored).
  const savedOriginalNameField = settings.originalNameField || null;
  const selectedOriginalNameFieldOption = savedOriginalNameField
    ? originalNameFieldOptions.find(
        (o) =>
          o.viewId === savedOriginalNameField.view_id &&
          o.fieldName === savedOriginalNameField.field_id
      ) ?? null
    : null;
  const categoryOptions = settingsQuery.data?.categoryOptions ?? [];
  // Options not yet claimed by an existing category row — both the "Add a new file name template"
  // button's visibility and each pending card's own inline `Select` are gated on this.
  const usedCategoryLabels = new Set(settings.categories.map((c) => c.label));
  const availableCategoryOptions = categoryOptions.filter(
    (option) => !usedCategoryLabels.has(option)
  );

  // One Changes-panel group PER CATEGORY, titled by the category's own name, rather than one
  // lumped "File Name Templates" group covering every category change at once. A category with no
  // saved counterpart (matched by `label` — categories have no other stable id) is a brand-new,
  // unsaved addition (`isNew: true`); one that exists in both but differs is a plain edit; one
  // that existed before but is gone now was removed this session — still surfaced.
  const categoryChangeGroups: SpotterChangeGroup[] = [
    ...settings.categories
      .map((category): SpotterChangeGroup | null => {
        const original = originalSettings.categories.find(
          (c) => c.label === category.label
        );
        if (original && settingsEqual(category, original)) {
          return null;
        }
        return {
          id: `category:${category.label}`,
          label: category.label,
          isNew: !original,
          items: [
            {
              id: "template",
              label: SPOTTER_STRINGS.rename.templateLabel,
            },
          ],
        };
      })
      .filter((group): group is SpotterChangeGroup => !!group),
    ...originalSettings.categories
      .filter(
        (original) =>
          !settings.categories.some((c) => c.label === original.label)
      )
      .map(
        (removed): SpotterChangeGroup => ({
          id: `category:${removed.label}`,
          label: removed.label,
          items: [
            {
              id: "removed",
              label: SPOTTER_STRINGS.rename.templateRemovedLabel,
            },
          ],
        })
      ),
  ];
  const optionChanges: { id: string; label: string }[] = [];
  if (settings.replaceAssetTitle !== originalSettings.replaceAssetTitle) {
    optionChanges.push({
      id: "replaceAssetTitle",
      label: SPOTTER_STRINGS.rename.replaceAssetTitleLabel,
    });
  }
  if (
    settings.includeFileExtensionInTitle !==
    originalSettings.includeFileExtensionInTitle
  ) {
    optionChanges.push({
      id: "includeFileExtensionInTitle",
      label: SPOTTER_STRINGS.rename.includeFileExtensionInTitleLabel,
    });
  }
  if (
    !settingsEqual(
      settings.originalNameField,
      originalSettings.originalNameField
    )
  ) {
    optionChanges.push({
      id: "originalNameField",
      label: SPOTTER_STRINGS.rename.originalNameFieldLabel,
    });
  }
  if (settings.useMetadataValues !== originalSettings.useMetadataValues) {
    optionChanges.push({
      id: "useMetadataValues",
      label: SPOTTER_STRINGS.rename.useMetadataValuesLabel,
    });
  }

  const changeGroups: SpotterChangeGroup[] = [
    ...categoryChangeGroups,
    ...(optionChanges.length
      ? [
          {
            id: "options",
            label: SPOTTER_STRINGS.rename.optionsTitle,
            items: optionChanges,
          },
        ]
      : []),
  ];

  const handleSave = (): void => {
    // `fieldTokens` is recomputed from the live view catalog right before every save, not carried
    // forward from `settings` state — see the file doc comment's "FIELD IDENTITY" section.
    saveMutation.mutate(
      { ...settings, fieldTokens: computeFieldTokens(flatFieldEntries) },
      {
        onSuccess: (saved) => {
          setSettings(saved.settings);
          setOriginalSettings(saved.settings);
        },
      }
    );
  };

  const handleCancel = (): void => {
    setSettings(originalSettings);
    saveMutation.reset();
  };

  // "Add a new file name template" button — always appends a fresh pending card, expanded and
  // scrolled into view. Stays clickable even while an earlier pending card is still unresolved.
  const handleAddTemplate = (): void => {
    const id = `${PENDING_PREFIX}${pendingIdCounterRef.current++}`;
    setPendingCategoryIds((prev) => [...prev, id]);
    setJustAddedPendingId(id);
    setExpandedCategoryKeys((prev) => new Set(prev).add(id));
  };

  // The actual removal, run once `PendingCategoryRow`'s exit animation finishes — see
  // `removingCategoryKeys`'s own doc comment for why this isn't run directly on click.
  const handleRemovePendingCategory = (id: string): void => {
    setPendingCategoryIds((prev) => prev.filter((p) => p !== id));
    setRemovingCategoryKeys((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setExpandedCategoryKeys((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  // A pending card's category `Select` resolves it into a real `CategoryRow` — appends to
  // `settings.categories` and drops the pending id, same net effect the old single "Add" `Select`
  // used to have in one step. The resolved row inherits the pending card's expanded state so it
  // doesn't visibly collapse-then-reopen.
  const handleResolvePendingCategory = (id: string, label: string): void => {
    setSettings((prev) => ({
      ...prev,
      categories: [...prev.categories, { label, template: "" }],
    }));
    setPendingCategoryIds((prev) => prev.filter((p) => p !== id));
    setJustAddedCategoryLabel(label);
    setExpandedCategoryKeys((prev) => {
      const next = new Set(prev);
      next.delete(id);
      next.add(label);
      return next;
    });
  };

  const changesProps: SpotterChangesPanelProps = {
    groups: changeGroups,
    onSave: handleSave,
    onCancel: handleCancel,
    isSaving: saveMutation.isLoading,
    saveError: saveMutation.isError ? SPOTTER_STRINGS.rename.saveError : null,
    saveSuccess: saveMutation.isSuccess,
    saveDisabledReason: settings.originalNameField
      ? null
      : SPOTTER_STRINGS.rename.originalNameFieldRequiredError,
  };

  return (
    <Page>
      <SpotterHeader changes={changesProps} />
      <Wrap>
        <SelectPortalHost ref={setSelectPortal} />
        <PageWrapperTwoColumn
          minWidth="1030px"
          id="spotter-rename"
          padding="0"
          defaultRatio={0.72}
          stickyTop="var(--spotterHeaderHeight, 98px)"
        >
          <Main>
            <Note>
              <NoteRule />
              <div>{SPOTTER_STRINGS.rename.note}</div>
            </Note>

            <Section title={SPOTTER_STRINGS.rename.optionsTitle}>
              <SectionBody>
                <OptionsGrid>
                  <OptionsCheckboxes>
                    <Checkbox
                      inlineLabel
                      label={SPOTTER_STRINGS.rename.replaceAssetTitleLabel}
                      value={settings.replaceAssetTitle}
                      onChange={(replaceAssetTitle) =>
                        setSettings({ ...settings, replaceAssetTitle })
                      }
                      disabled={saveMutation.isLoading}
                    />
                    <Checkbox
                      inlineLabel
                      label={
                        SPOTTER_STRINGS.rename.includeFileExtensionInTitleLabel
                      }
                      value={settings.includeFileExtensionInTitle}
                      onChange={(includeFileExtensionInTitle) =>
                        setSettings({
                          ...settings,
                          includeFileExtensionInTitle,
                        })
                      }
                      // Only meaningful once the asset title is actually being replaced —
                      // there's no "patched title" to keep the extension on otherwise.
                      disabled={
                        saveMutation.isLoading || !settings.replaceAssetTitle
                      }
                    />
                    <Checkbox
                      inlineLabel
                      label={SPOTTER_STRINGS.rename.useMetadataValuesLabel}
                      value={settings.useMetadataValues}
                      onChange={(useMetadataValues) =>
                        setSettings({ ...settings, useMetadataValues })
                      }
                      disabled={saveMutation.isLoading}
                    />
                  </OptionsCheckboxes>
                  <Select
                    label={SPOTTER_STRINGS.rename.originalNameFieldLabel}
                    description={
                      SPOTTER_STRINGS.rename.originalNameFieldDescription
                    }
                    required
                    isSearchable
                    options={originalNameFieldOptions}
                    placeholder={
                      SPOTTER_STRINGS.rename.originalNameFieldPlaceholder
                    }
                    value={selectedOriginalNameFieldOption}
                    onChange={(option) => {
                      const composite = (option as SelectOption)?.value as
                        | string
                        | undefined;
                      const parsed = composite
                        ? parseFieldOptionValue(composite)
                        : undefined;
                      setSettings({
                        ...settings,
                        originalNameField: parsed
                          ? {
                              field_id: parsed.fieldName,
                              scope: parsed.viewScope,
                              view_id: parsed.viewId,
                            }
                          : "",
                      });
                    }}
                    onClear={() =>
                      setSettings({ ...settings, originalNameField: "" })
                    }
                    portalTarget={selectPortal}
                  />
                </OptionsGrid>
              </SectionBody>
            </Section>

            <TemplatesHeaderRow>
              <TemplatesSectionHeader>
                {SPOTTER_STRINGS.rename.templatesSectionTitle}
                {availableCategoryOptions.length === 0 && (
                  <AllCategoriesAddedNote>
                    {SPOTTER_STRINGS.rename.allCategoriesAddedMessage}
                  </AllCategoriesAddedNote>
                )}
              </TemplatesSectionHeader>
              {availableCategoryOptions.length > 0 && (
                <AddTemplateWrap>
                  <Button
                    label={SPOTTER_STRINGS.rename.addTemplateButton}
                    icon={PlusCircle}
                    type="success"
                    onClick={handleAddTemplate}
                  />
                </AddTemplateWrap>
              )}
            </TemplatesHeaderRow>
            {settings.categories.map((category) => {
              const originalCategory = originalSettings.categories.find(
                (c) => c.label === category.label
              );
              // Keyed by `label`, not array index — removal is now deferred until the card's
              // own exit animation finishes (see `removingCategoryKeys`'s doc comment), so the
              // array itself doesn't shift out from under a still-animating row.
              return (
                <CategoryRow
                  key={category.label}
                  value={category}
                  onChange={(next) =>
                    setSettings({
                      ...settings,
                      categories: settings.categories.map((c) =>
                        c.label === category.label ? next : c
                      ),
                    })
                  }
                  onRemove={() =>
                    setRemovingCategoryKeys((prev) =>
                      new Set(prev).add(category.label)
                    )
                  }
                  onRemoveTransitionEnd={() => {
                    setSettings((prev) => ({
                      ...prev,
                      categories: prev.categories.filter(
                        (c) => c.label !== category.label
                      ),
                    }));
                    setRemovingCategoryKeys((prev) => {
                      const next = new Set(prev);
                      next.delete(category.label);
                      return next;
                    });
                    setExpandedCategoryKeys((prev) => {
                      const next = new Set(prev);
                      next.delete(category.label);
                      return next;
                    });
                  }}
                  isRemoving={removingCategoryKeys.has(category.label)}
                  fieldEntries={mappedFieldEntries}
                  allFieldEntries={flatFieldEntries}
                  isNew={!originalCategory}
                  isEdited={
                    !!originalCategory &&
                    !settingsEqual(category, originalCategory)
                  }
                  autoFocus={category.label === justAddedCategoryLabel}
                  onAutoFocused={() => setJustAddedCategoryLabel(null)}
                  expanded={expandedCategoryKeys.has(category.label)}
                  onToggleExpand={() => toggleExpandedCategory(category.label)}
                />
              );
            })}
            {/* Trailing block: every card added via the "Add a new file name template" button,
                still unresolved, in the order it was added — mirrors Field Mapping's own
                trailing `newFieldOrder` block. Resolving one (picking a category) replaces it
                with a normal `CategoryRow` above on the next render, via
                `handleResolvePendingCategory`. */}
            {pendingCategoryIds.map((id) => (
              <PendingCategoryRow
                key={id}
                availableCategoryOptions={availableCategoryOptions}
                onResolve={(label) => handleResolvePendingCategory(id, label)}
                onRemove={() =>
                  setRemovingCategoryKeys((prev) => new Set(prev).add(id))
                }
                onRemoveTransitionEnd={() => handleRemovePendingCategory(id)}
                isRemoving={removingCategoryKeys.has(id)}
                isJustAdded={id === justAddedPendingId}
                onJustAddedHandled={() => setJustAddedPendingId(null)}
                selectPortal={selectPortal}
                expanded={expandedCategoryKeys.has(id)}
                onToggleExpand={() => toggleExpandedCategory(id)}
              />
            ))}
          </Main>
          <Aside>
            <AsideChangesPanel>
              <SpotterChangesPanel {...changesProps} />
            </AsideChangesPanel>
          </Aside>
        </PageWrapperTwoColumn>
      </Wrap>
    </Page>
  );
};

export default RenameScreen;
